import "./Home.css";
import { useState, useEffect, useCallback } from "react";
import VoucherCard from "./VoucherCard";
import Login from "./Login";
import VoucherCertificate from "./VoucherCertificate";
import PreredemptionModal from "./PreredemptionModal";
import { AnimatePresence, motion } from "framer-motion";
import Container from "react-bootstrap/Container";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navigation from "./Navigation";
import DeleteConfirmModal from "./DeleteConfirmModal";
import Button from "react-bootstrap/Button";
import { jwtDecode } from "jwt-decode";
import Spinner from "react-bootstrap/Spinner";
import { Link } from "react-router-dom";
import Alert from "react-bootstrap/Alert";

const apiPrefix = process.env.REACT_APP_API_PREFIX;
const buildHash = process.env.REACT_APP_BUILD_HASH || "DEV";

const isUUID = (str) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const Home = ({ accessToken, setAccessToken }) => {
  const [vouchers, setVouchers] = useState(null);
  const [showVoucherCertificate, setShowVoucherCertificate] = useState(false);
  const [voucherCertificateData, setVoucherCertificateData] = useState(null);
  const [voucherValidationData, setVoucherValidationData] = useState({
    id: "",
    title: "",
    description: "",
    location: "",
    link: "",
  });
  const [showPreredemptionModal, setShowPreredemptionModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState({
    id: "",
    title: "",
    location: "",
  });
  const [pendingVoucher, setPendingVoucher] = useState();
  const [isNewUser, setIsNewUser] = useState(false);

  const removeVoucher = async (voucherId) => {
    try {
      const response = await fetch(`${apiPrefix}/voucher/${voucherId}`, {
        method: "DELETE",
        headers: new Headers({
          "ngrok-skip-browser-warning": true,
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      if (response.ok) {
        setVouchers(vouchers.filter((voucher) => voucher.id !== voucherId));
      } else if (response.status === 403) {
        handleLogout();
      } else {
        toast.error(
          "Der Gutschein konnte nicht aus deiner Liste gelöscht werden. Bitte versuche es erneut."
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const preRedeemVoucher = async (voucherId) => {
    const res = await fetch(`${apiPrefix}/validate/${voucherId}`, {
      headers: new Headers({
        "ngrok-skip-browser-warning": true,
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    if (res.ok) {
      const json = await res.json();
      if (json.status === "valid") {
        setVoucherValidationData({ ...json, id: voucherId });
        setShowPreredemptionModal(true);
      } else if (json.status === "expired") {
        toast.error(
          `Dieser Gutschein ist am ${new Date(
            json.expiration
          ).toLocaleDateString("de", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })} abgelaufen.`
        );
      } else if (json.status === "redeemed") {
        toast.error("Dieser Gutschein wurde bereits eingelöst.");
      } else if (json.status === "claimed") {
        toast.error(
          "Dieser Gutschein wurde bereits von einer anderen Person beansprucht."
        );
      } else {
        toast.error("Dies ist kein gültiger Gutschein.");
      }
    } else if (res.status === 403) {
      handleLogout();
    }
  };

  const redeemVoucher = async (voucherId, controlNumber) => {
    const res = await fetch(
      `${apiPrefix}/redeem/${voucherId}?ctrl=${controlNumber}`,
      {
        headers: new Headers({
          "ngrok-skip-browser-warning": true,
          Authorization: `Bearer ${accessToken}`,
        }),
      }
    );
    if (res.ok) {
      const json = await res.json();
      if (json.status === "redemption") {
        setVoucherCertificateData(json);
        setShowVoucherCertificate(true);
        setVouchers(vouchers.filter((voucher) => voucher.id !== voucherId));
      } else if (json.status === "expired") {
        toast.error(
          `Dieser Gutschein ist am ${new Date(
            json.expiration
          ).toLocaleDateString("de", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })} abgelaufen.`
        );
      } else if (json.status === "redeemed") {
        toast.error("Dieser Gutschein wurde bereits eingelöst.");
      } else if (json.status === "claimed") {
        toast.error(
          "Dieser Gutschein wurde bereits von einer anderen Person beansprucht."
        );
      } else if (json.status === "invalid") {
        toast.error("Dies ist kein gültiger Gutschein.");
      } else if (json.status === "control") {
        toast.error(
          `Ungültiger PIN. Den PIN erhältst du beim Einlösen in ${voucherValidationData.location} vom Personal.`
        );
      }
    } else if (res.status === 403) {
      handleLogout();
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("jwt-token");
    setAccessToken(null);
    setVouchers(null);
  }, [setAccessToken]);

  const userIdentifier = (token) => {
    try {
      return jwtDecode(token).identifier.substring(0, 8);
    } catch (err) {}
    return "anonym";
  };

  useEffect(() => {
    if (accessToken) {
      const loadVouchers = async () => {
        const res = await fetch(`${apiPrefix}/voucher`, {
          headers: new Headers({
            "ngrok-skip-browser-warning": true,
            Authorization: `Bearer ${accessToken}`,
          }),
        });
        if (res.ok) {
          const json = await res.json();
          setVouchers(json);
        } else if (res.status === 403) {
          handleLogout();
        }
      };
      loadVouchers();
    }
  }, [accessToken, handleLogout]);

  useEffect(() => {
    const addVoucher = async (voucherId) => {
      const res = await fetch(`${apiPrefix}/validate/${voucherId}`, {
        headers: new Headers({
          "ngrok-skip-browser-warning": true,
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      if (res.ok) {
        const json = await res.json();
        if (json.status === "valid") {
          const voucherAlreadyExists = vouchers.find(
            (item) => item.id === voucherId
          );
          if (voucherAlreadyExists) {
            toast.warn(
              `Der Gutschein "${json.title}" von ${json.location} ist bereits in der Liste.`
            );
          } else {
            try {
              const response = await fetch(
                `${apiPrefix}/voucher/${voucherId}`,
                {
                  method: "PUT",
                  headers: new Headers({
                    "ngrok-skip-browser-warning": true,
                    Authorization: `Bearer ${accessToken}`,
                  }),
                }
              );
              if (response.ok) {
                setVouchers((vouchers) => [
                  ...vouchers,
                  {
                    id: voucherId,
                    title: json.title,
                    description: json.description,
                    location: json.location,
                    link: json.link,
                    expiration: json.expiration,
                    newlyAdded: true,
                  },
                ]);
              } else if (response.status === 403) {
                handleLogout();
              } else {
                toast.error(
                  "Der Gutschein konnte deiner Liste nicht hinzugefügt werden. Bitte versuche es erneut."
                );
              }
            } catch (err) {
              console.log(err);
            }
          }
        } else if (json.status === "expired") {
          toast.error(
            `Der Gutschein "${json.title}" von ${
              json.location
            } ist am ${new Date(json.expiration).toLocaleDateString("de", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })} abgelaufen.`
          );
        } else if (json.status === "redeemed") {
          toast.error(
            `Der Gutschein "${json.title}" von ${json.location} wurde bereits eingelöst.`
          );
        } else if (json.status === "claimed") {
          toast.error(
            `Der Gutschein "${json.title}" von ${json.location} wurde bereits von einer anderen Person beansprucht.`
          );
        } else if (json.status === "bundle") {
          json.vouchers.forEach(addVoucher);
        } else {
          toast.error("Dies ist kein gültiger Gutschein.");
        }
      } else if (res.status === 403) {
        handleLogout();
      }
    };

    if (accessToken && vouchers && pendingVoucher) {
      addVoucher(pendingVoucher);
      setPendingVoucher(null);
    }
  }, [accessToken, pendingVoucher, vouchers, handleLogout]);

  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split("/").filter(Boolean);
    if (pathParts.length === 1 && isUUID(pathParts[0])) {
      setPendingVoucher(pathParts[0]);
      window.history.pushState(null, "", "/");
    }
  }, [setPendingVoucher]);

  if (!accessToken) {
    return (
      <div className="page-background">
        <ToastContainer position="top-center" autoClose="10000" />
        <Login setToken={setAccessToken} setIsNewUser={setIsNewUser} />
      </div>
    );
  }

  return (
    <div className="page-background">
      <ToastContainer position="top-center" autoClose="10000" />

      <DeleteConfirmModal
        showDeleteConfirmModal={showDeleteConfirmModal}
        setShowDeleteConfirmModal={setShowDeleteConfirmModal}
        deleteConfirmData={deleteConfirmData}
        removeVoucher={removeVoucher}
      />

      <PreredemptionModal
        showPreredemptionModal={showPreredemptionModal}
        setShowPreredemptionModal={setShowPreredemptionModal}
        voucherValidationData={voucherValidationData}
        redeemVoucher={redeemVoucher}
      />

      <VoucherCertificate
        showVoucherCertificate={showVoucherCertificate}
        setShowVoucherCertificate={setShowVoucherCertificate}
        voucherCertificateData={voucherCertificateData}
      />

      <Navigation />

      <Container className="p-3">
        {isNewUser && (
          <Alert variant="warning" dismissible>
            {/* <Alert.Heading>Lesezeichen erstellen</Alert.Heading> */}
            Deine Gutscheine sind unter der Adresse <b>buono.app</b> aufrufbar.
            Erstelle ein Lesezeichen von dieser Seite, damit du jederzeit
            schnell darauf zugreifen kannst.
          </Alert>
        )}

        <AnimatePresence initial={false} mode="popLayout">
          {vouchers &&
            vouchers.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring" }}
              >
                <VoucherCard
                  item={item}
                  setShowDeleteConfirmModal={setShowDeleteConfirmModal}
                  setDeleteConfirmData={setDeleteConfirmData}
                  redeemVoucher={preRedeemVoucher}
                ></VoucherCard>
              </motion.div>
            ))}
          {vouchers && vouchers.length === 0 && (
            <h3 className="text-center text-large p-5">
              Du hast zur Zeit leider keine Gutscheine in der Liste
            </h3>
          )}
          {vouchers === null && (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          )}
        </AnimatePresence>

        <motion.div layout>
          <div className="text-center">
            <span className="text-muted" style={{ fontSize: "12px" }}>
              <p className="mb-0">Version 1.0.0 ({buildHash})</p>
              <p className="mb-0">
                Identifikation: {userIdentifier(accessToken)}
              </p>
              <p>
                <Link to="/policy">AGB, Datenschutz, Impressum</Link>
              </p>
            </span>
          </div>
          <div className="mt-3 text-center">
            <Button
              size="sm"
              className="mb-4"
              variant="outline-secondary"
              onClick={() => handleLogout()}
            >
              Abmelden
            </Button>
          </div>
        </motion.div>
      </Container>
    </div>
  );
};

export default Home;
